import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DurationInput } from '@/modules/common/components/molecules/inputs/duration-input';

/** A time-off day can't exceed a standard working day (8h). */
const MAX_HOURLY_MINUTES = 8 * 60;

interface TimeOffHourlyInputProps<T extends FieldValues> {
  control: Control<T>;
  /** Field name storing the day's minutes value. */
  name: Path<T>;
  disabled: boolean;
}

/**
 * RHF wrapper around the shared {@link DurationInput} for HOURLY days. Stores the
 * parsed minutes in the form (0 on invalid input, so the day fails validation and
 * the submit stays disabled). Inline error appears on blur, then live. Caps the
 * value at {@link MAX_HOURLY_MINUTES} (8h per day).
 */
export const TimeOffHourlyInput = <T extends FieldValues>({
  control,
  name,
  disabled,
}: TimeOffHourlyInputProps<T>) => {
  const { t } = useTranslation('authenticated.timeOff');
  const { field } = useController({ control, name });
  const minutes = (field.value as number | undefined) ?? null;

  return (
    <DurationInput
      value={minutes}
      onChange={value => field.onChange(value)}
      onBlurParse={() => field.onBlur()}
      error={t('details.duration.invalidHourly')}
      maxMinutes={MAX_HOURLY_MINUTES}
      maxError={t('details.duration.exceedsDay')}
      placeholder={t('details.duration.hourlyPlaceholder')}
      ariaLabel={t('details.duration.hourly')}
      disabled={disabled}
    />
  );
};
